import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ProfileModal from './ProfileModal';
import { SidebarPlanBadge } from './PlanStatusCard';
import TrialBanner from './TrialBanner';
import OnboardingWizard from './OnboardingWizard';
import axios from 'axios';
import { API } from '../lib/api';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  CheckCircle,
  CreditCard,
  Megaphone,
  LogOut,
  Moon,
  Sun,
  Dumbbell,
  X,
  ChevronRight,
  ChevronLeft,
  UserCog,
  User,
  Building2,
  Settings,
  Plus,
  UserPlus,
  ClipboardCheck,
  BarChart2,
  Search,
  Bell,
  Menu,
  Keyboard,
  ShoppingCart,
  CalendarDays,
  MessageCircle,
  FlaskConical,
  Package,
  Target,
  TrendingUp,
  Smartphone,
} from 'lucide-react';
import { useKeyboardShortcuts, SHORTCUTS } from '../hooks/useKeyboardShortcuts';

const navGroups = [
  {
    label: 'Overview',
    items: [{ path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Management',
    items: [
      { path: '/admin/members', label: 'Members', icon: Users },
      { path: '/admin/trainers', label: 'Trainers', icon: UserCog },
      { path: '/admin/attendance', label: 'Attendance', icon: CheckCircle },
      { path: '/admin/payments', label: 'Payments', icon: CreditCard },
      { path: '/admin/classes', label: 'Classes', icon: CalendarDays },
      { path: '/admin/pos', label: 'Point of Sale', icon: ShoppingCart },
    ],
  },
  {
    label: 'Growth',
    items: [
      { path: '/admin/leads', label: 'Lead Pipeline', icon: Target },
      { path: '/admin/trials', label: 'Trials', icon: FlaskConical },
      { path: '/admin/whatsapp', label: 'WhatsApp', icon: MessageCircle },
      { path: '/admin/plans', label: 'Plans', icon: Package },
      { path: '/admin/member-app', label: 'Member App', icon: Smartphone },
    ],
  },
  {
    label: 'Insights',
    items: [
      { path: '/admin/reports', label: 'Reports', icon: BarChart2 },
      { path: '/admin/announcements', label: 'Announcements', icon: Megaphone },
    ],
  },
];

const allNavItems = navGroups.flatMap(g => g.items);

const bottomTabs = [
  { path: '/admin/dashboard', label: 'Home', icon: LayoutDashboard },
  { path: '/admin/members', label: 'Members', icon: Users },
  { path: '/admin/attendance', label: 'Attendance', icon: CheckCircle },
  { path: '/admin/payments', label: 'Payments', icon: CreditCard },
  { path: '/admin/reports', label: 'Reports', icon: BarChart2 },
];

function getInitials(name) {
  return (name || 'G O')
    .split(' ')
    .map(w => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function UserAvatar({ name, photo, size = 36, onClick, className = '' }) {
  const hue = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Account"
      className={`relative flex-shrink-0 flex items-center justify-center rounded-full text-white font-bold overflow-hidden cursor-pointer shadow-md transform transition-transform hover:scale-105 ${className}`}
      style={{
        width: size,
        height: size,
        border: 'none',
        background: photo
          ? 'transparent'
          : `linear-gradient(135deg, hsl(${hue},65%,52%), hsl(${(hue + 40) % 360},60%,45%))`,
        fontSize: Math.max(10, size * 0.36),
      }}
    >
      {photo ? (
        <img src={photo} alt="avatar" className="w-full h-full object-cover" />
      ) : (
        getInitials(name)
      )}
    </button>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    let timer;
    if (open && inputRef.current) {
      timer = setTimeout(() => inputRef.current?.focus(), 50);
    }
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    const handler = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(
          `${API}/members/search?q=${encodeURIComponent(query.trim())}&limit=6`
        );
        const filtered = Array.isArray(res.data) ? res.data : [];
        setResults(
          filtered.map(m => ({
            type: 'member',
            id: m.id,
            label: m.name,
            sub: m.phone,
            path: `/admin/members/${m.id}`,
          }))
        );
      } catch {
        setResults([]);
        toast.error('Failed to fetch search results. Please try again.');
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const go = path => {
    navigate(path);
    setOpen(false);
    setQuery('');
    setResults([]);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-colors ${open ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'bg-gray-50 dark:bg-[#0f0f0f] border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-emerald-500 hover:text-emerald-500 dark:hover:border-emerald-500 dark:hover:text-emerald-400'}`}
      >
        <Search size={14} />
        <span className="hidden sm:inline">Search...</span>
        <kbd className="hidden sm:inline-block px-1.5 py-0.5 rounded text-[10px] bg-gray-200 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 ml-1">
          ⌘K
        </kbd>
      </button>

      {open && (
        <div className="absolute top-12 right-0 w-[360px] rounded-xl bg-white/90 dark:bg-[#0a0a0a]/80 backdrop-blur-xl border border-gray-200 dark:border-white/10 shadow-xl z-[999] overflow-hidden drop-in">
          <div className="p-3 border-b border-gray-100 dark:border-white/5 flex items-center gap-2">
            <Search size={16} className="text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search members by name, phone..."
              className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder:text-gray-400"
            />
            {searching && (
              <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            )}
          </div>
          {results.length > 0 && (
            <div className="max-h-[280px] overflow-y-auto py-2">
              {results.map(r => (
                <button
                  key={r.id}
                  onClick={() => go(r.path)}
                  className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Users size={14} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                      {r.label}
                    </p>
                    {r.sub && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{r.sub}</p>
                    )}
                  </div>
                  <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
          {query && !searching && results.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No members found for "{query}"
            </div>
          )}
          {!query && (
            <div className="p-4 bg-gray-50 dark:bg-[#0a0a0a] text-center">
              <p className="text-xs font-semibold text-gray-500 mb-1">Quick Search</p>
              <p className="text-xs text-gray-400">Type a name, phone, or email</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ALERT_CONFIG = {
  overdue_payment: { color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-500/10', icon: CreditCard },
  expiring_membership: {
    color: 'text-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-500/10',
    icon: Users,
  },
  trial_expiring: {
    color: 'text-purple-500',
    bg: 'bg-purple-50 dark:bg-purple-500/10',
    icon: FlaskConical,
  },
  lead_followup: { color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-500/10', icon: Target },
};

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [count, setCount] = useState(0);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let ws = null;
    let reconnectTimer = null;

    const connectWs = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const wsProtocol = API.startsWith('https') ? 'wss:' : 'ws:';
      const host = API.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}//${host}/notifications/ws?token=${token}`;

      ws = new WebSocket(wsUrl);

      ws.onmessage = event => {
        if (event.data === 'pong') {
          return;
        }
        try {
          const data = JSON.parse(event.data);
          if (data.alerts !== undefined) {
            setAlerts(data.alerts || []);
            setCount(data.count || 0);
          }
        } catch {}
      };

      ws.onclose = () => {
        reconnectTimer = setTimeout(connectWs, 5000);
      };
    };

    connectWs();

    const pingInterval = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer);
      if (ws) {
        ws.close();
      }
    };
  }, []);

  useEffect(() => {
    const handler = e => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleAlert = alert => {
    setOpen(false);
    if (alert.member_id) {
      navigate(`/admin/members/${alert.member_id}`);
    } else if (alert.trial_id) {
      navigate('/admin/trials');
    } else if (alert.lead_id) {
      navigate('/admin/leads');
    }
  };

  return (
    <div ref={panelRef} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={`relative w-9 h-9 flex items-center justify-center rounded-xl border transition-colors ${open ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/40 text-amber-600 dark:text-amber-400' : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-white/10 dark:hover:text-white'}`}
      >
        <Bell size={16} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center shadow-sm">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute top-12 right-0 w-80 sm:w-96 rounded-2xl bg-white/90 dark:bg-[#0f0f0f]/80 backdrop-blur-xl border border-gray-200 dark:border-white/10 shadow-2xl z-[999] overflow-hidden drop-in">
          <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/5">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-amber-500" />
              <p className="font-black text-sm text-gray-900 dark:text-white">Alerts</p>
              {count > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-500/10 text-red-500 border border-red-100 dark:border-red-500/20">
                  {count}
                </span>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10"
            >
              <X size={14} />
            </button>
          </div>
          <div className="overflow-y-auto max-h-80 custom-scrollbar">
            {alerts.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Bell size={20} className="text-emerald-500" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white">All clear!</p>
                <p className="text-xs text-gray-400 mt-0.5">No pending alerts right now.</p>
              </div>
            ) : (
              alerts.map((a, i) => {
                const cfg = ALERT_CONFIG[a.type] || ALERT_CONFIG.expiring_membership;
                const Icon = cfg.icon;
                return (
                  <button
                    key={i}
                    onClick={() => handleAlert(a)}
                    className="w-full text-left flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors border-b border-gray-50 dark:border-white/5 last:border-0"
                  >
                    <div
                      className={`w-8 h-8 rounded-xl ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}
                    >
                      <Icon size={14} className={cfg.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-900 dark:text-white truncate">
                        {a.label}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {a.detail}
                      </p>
                    </div>
                    <ChevronRight size={12} className="text-gray-400 flex-shrink-0 mt-1" />
                  </button>
                );
              })
            )}
          </div>
          {alerts.length > 0 && (
            <div className="p-3 border-t border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-white/[0.02]">
              <p className="text-[10px] text-center text-gray-400 flex items-center justify-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Live via WebSockets
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SidebarContent({
  onClose,
  gymName,
  gymLogo,
  ownerName,
  profilePhoto,
  location,
  theme,
  toggleTheme,
  handleLogout,
  openProfile,
  collapsed,
  onToggleCollapse,
}) {
  const isDark = theme === 'dark';

  return (
    <aside
      data-testid="admin-sidebar"
      className={`${collapsed ? 'w-[72px]' : 'w-[260px]'} min-h-screen bg-white dark:bg-[#0c0f14] flex flex-col flex-shrink-0 relative z-10 border-r border-gray-200 dark:border-white/[0.07] transition-[width] duration-200 ease-out overflow-hidden`}
    >
      {/* Logo */}
      <div
        className={`border-b border-gray-100 dark:border-white/[0.05] flex items-center flex-shrink-0 ${collapsed ? 'justify-center py-[18px] px-2' : 'justify-between p-5'}`}
      >
        <div className={`flex items-center ${collapsed ? '' : 'gap-3'} min-w-0`}>
          {gymLogo ? (
            <img
              src={gymLogo}
              alt="gym logo"
              className="w-10 h-10 rounded-xl object-cover shadow-md flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md flex-shrink-0 shadow-emerald-500/20">
              <Dumbbell size={20} className="text-white" />
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-gray-900 dark:text-white font-black text-[15px] truncate tracking-tight">
                {gymName || 'Fitxero'}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                <p className="text-gray-500 dark:text-gray-400 text-[10px] uppercase font-bold tracking-wider">
                  Smart Gym OS
                </p>
              </div>
            </div>
          )}
        </div>
        {onClose && !collapsed && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-md bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/20 transition-colors ml-2 flex-shrink-0"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav data-testid="admin-nav" className="flex-1 p-2 flex flex-col gap-3 overflow-y-auto">
        {navGroups.map((group, gi) => (
          <div key={gi}>
            {!collapsed ? (
              <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {group.label}
              </p>
            ) : (
              gi > 0 && (
                <div className="mx-auto w-8 border-t border-gray-200 dark:border-white/10 my-1" />
              )
            )}
            <div className="flex flex-col gap-0.5">
              {group.items.map(item => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    title={collapsed ? item.label : undefined}
                    className={`relative flex items-center gap-3 py-2.5 rounded-xl text-sm transition-all duration-200 ease-out overflow-hidden ${
                      collapsed ? 'justify-center px-2' : 'pl-3 pr-3'
                    } ${
                      isActive
                        ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold'
                        : 'text-gray-600 dark:text-gray-400 font-medium hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-[20%] bottom-[20%] w-[3px] bg-emerald-500 rounded-r-full" />
                    )}
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                        isActive
                          ? 'bg-emerald-500/20 dark:bg-emerald-400/15'
                          : 'bg-gray-100 dark:bg-white/5'
                      }`}
                    >
                      <Icon
                        size={16}
                        className={
                          isActive
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }
                      />
                    </div>
                    {!collapsed && <span className="flex-1">{item.label}</span>}
                    {!collapsed && isActive && <ChevronRight size={14} className="opacity-50" />}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* Collapse toggle — desktop only */}
        {!onClose && onToggleCollapse && (
          <div className="mt-auto pt-2">
            <button
              onClick={onToggleCollapse}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={`flex items-center gap-3 py-2 rounded-xl text-xs font-semibold text-gray-500 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-700 dark:hover:text-gray-300 transition-colors w-full ${
                collapsed ? 'justify-center px-0' : 'px-3'
              }`}
            >
              <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              </div>
              {!collapsed && <span>Collapse</span>}
            </button>
          </div>
        )}
      </nav>

      {/* Plan Badge */}
      {!collapsed && <SidebarPlanBadge />}

      {/* Bottom User Section */}
      <div
        className={`border-t border-gray-100 dark:border-white/[0.05] flex flex-col gap-1 ${collapsed ? 'p-2' : 'p-3'}`}
      >
        {/* User Card */}
        {!collapsed ? (
          <div
            onClick={() => openProfile('profile')}
            className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer transition-colors mb-2"
          >
            <UserAvatar name={ownerName} photo={profilePhoto} size={36} />
            <div className="min-w-0 flex-1">
              <p className="text-gray-900 dark:text-white font-bold text-sm truncate">
                {ownerName}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Gym Owner</p>
            </div>
            <Settings size={14} className="text-gray-400 flex-shrink-0" />
          </div>
        ) : (
          <div
            onClick={() => openProfile('profile')}
            className="flex justify-center py-2 cursor-pointer"
            title={ownerName}
          >
            <UserAvatar name={ownerName} photo={profilePhoto} size={36} />
          </div>
        )}

        {/* Theme Toggle */}
        {!collapsed ? (
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition-colors w-full text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center">
              {isDark ? <Sun size={14} /> : <Moon size={14} />}
            </div>
            {isDark ? 'Light Mode' : 'Dark Mode'}
          </button>
        ) : (
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            title={isDark ? 'Light Mode' : 'Dark Mode'}
            className="flex justify-center py-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors w-full"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        )}

        {/* Logout */}
        {!collapsed ? (
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors w-full text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
              <LogOut size={14} />
            </div>
            Logout
          </button>
        ) : (
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title="Logout"
            className="flex justify-center py-2 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors w-full"
          >
            <LogOut size={16} />
          </button>
        )}
      </div>
    </aside>
  );
}

export const AdminLayout = ({ children }) => {
  const { user, gym, logout, refreshGym } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const currentLocation = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [profileTab, setProfileTab] = useState('profile');
  const [fabOpen, setFabOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const openProfile = tab => {
    setProfileTab(tab);
    setProfileModal(true);
    setDropdownOpen(false);
  };

  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setFabOpen(false);
  }, [currentLocation.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const { showHelp, setShowHelp } = useKeyboardShortcuts();

  const currentNav = allNavItems.find(
    n => currentLocation.pathname === n.path || currentLocation.pathname.startsWith(n.path + '/')
  );
  const pageTitle = currentNav?.label || 'Dashboard';

  const gymName = gym?.gym_name || user?.gym_name || 'My Gym';
  const ownerName = gym?.owner_name || user?.owner_name || gymName;
  const profilePhoto = gym?.profile_photo || null;
  const gymLogo = gym?.gym_logo || null;
  const isDark = theme === 'dark';
  const isDashboard = currentLocation.pathname.endsWith('/dashboard');

  const dropdownItems = [
    { icon: User, label: 'My Profile', tab: 'profile' },
    { icon: Building2, label: 'Gym Information', tab: 'gym-info' },
    { icon: Settings, label: 'Account Settings', tab: 'settings' },
  ];

  const fabActions = [
    { icon: UserPlus, label: 'Add Member', path: '/admin/members/new' },
    { icon: ClipboardCheck, label: 'Attendance', path: '/admin/attendance' },
    { icon: CreditCard, label: 'Add Payment', path: '/admin/payments' },
    { icon: Megaphone, label: 'Announcement', path: '/admin/announcements' },
  ];

  const sidebarProps = {
    gymName,
    gymLogo,
    ownerName,
    profilePhoto,
    location: currentLocation,
    theme,
    toggleTheme,
    handleLogout,
    openProfile,
    collapsed: sidebarCollapsed,
    onToggleCollapse: () => setSidebarCollapsed(v => !v),
  };

  return (
    <div
      className="flex h-screen overflow-hidden bg-gray-50 dark:bg-[#0a0a0a] text-gray-900 dark:text-white font-sans selection:bg-blue-500/30"
      data-testid="admin-layout"
    >
      {/* Desktop Sidebar */}
      <div className="hidden md:flex">
        <SidebarContent onClose={null} {...sidebarProps} />
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative z-10 w-[260px] max-w-[85vw] h-full shadow-xl animate-in slide-in-from-left duration-200">
            <SidebarContent onClose={() => setMobileOpen(false)} {...sidebarProps} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6 h-[60px] sm:h-[68px] bg-white/90 dark:bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-gray-200 dark:border-white/10 flex-shrink-0 shadow-sm dark:shadow-none transition-colors">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2 -ml-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-sm font-semibold text-gray-500 dark:text-gray-400">
                {gymName}
              </span>
              <ChevronRight size={14} className="hidden sm:inline text-gray-400" />
              <span className="text-lg font-black tracking-tight text-gray-900 dark:text-white">
                {pageTitle}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <GlobalSearch />

            {/* Live Notification Bell */}
            <NotificationBell />

            <button
              onClick={toggleTheme}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {/* Avatar Dropdown */}
            <div
              ref={dropdownRef}
              className="relative flex items-center gap-3 ml-2 border-l border-gray-200 dark:border-white/10 pl-4"
            >
              <div className="hidden sm:block text-right">
                <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                  {gymName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Gym Owner</p>
              </div>
              <UserAvatar
                name={ownerName}
                photo={profilePhoto}
                onClick={() => setDropdownOpen(v => !v)}
              />

              {dropdownOpen && (
                <div className="absolute top-[52px] right-0 w-[240px] rounded-xl bg-white dark:bg-[#0f0f0f] border border-gray-200 dark:border-white/10 shadow-xl drop-in z-50 p-2">
                  <div className="p-3 border-b border-gray-100 dark:border-white/5 mb-2 flex items-center gap-3">
                    <UserAvatar name={ownerName} photo={profilePhoto} size={40} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                        {ownerName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Gym Owner</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {dropdownItems.map(item => (
                      <button
                        key={item.tab}
                        onClick={() => openProfile(item.tab)}
                        className="w-full text-left px-3 py-2 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors flex items-center gap-3"
                      >
                        <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                          <item.icon size={14} className="text-emerald-600 dark:text-emerald-400" />
                        </div>
                        {item.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-white/5">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex items-center gap-3"
                    >
                      <div className="w-7 h-7 rounded-lg bg-red-50 dark:bg-red-500/10 flex items-center justify-center flex-shrink-0">
                        <LogOut size={14} className="text-red-600" />
                      </div>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Keyboard hint */}
            <button
              onClick={() => setShowHelp(v => !v)}
              title="Keyboard shortcuts (?)"
              className="w-9 h-9 hidden md:flex items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
            >
              <Keyboard size={15} />
            </button>
          </div>
        </header>

        {/* Keyboard Shortcuts Overlay */}
        {showHelp && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center"
            onClick={() => setShowHelp(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div
              className="relative z-10 w-[90%] max-w-[440px] bg-white dark:bg-[#0f0f13] border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
                    <Keyboard size={17} className="text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-gray-900 dark:text-white">
                      Keyboard Shortcuts
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Navigate Fitxero faster
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHelp(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 grid grid-cols-1 gap-1.5">
                {SHORTCUTS.map(({ key, desc }) => (
                  <div
                    key={key}
                    className="flex items-center justify-between px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {desc}
                    </span>
                    <kbd className="px-2.5 py-1 rounded-lg text-xs font-bold bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 font-mono">
                      {key}
                    </kbd>
                  </div>
                ))}
              </div>
              <div className="px-4 pb-4">
                <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                  Press{' '}
                  <kbd className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 font-mono">
                    ?
                  </kbd>{' '}
                  to toggle this panel
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Page Content Wrapper */}
        <main
          className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar relative z-0"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <OnboardingWizard />
          <TrialBanner />
          <div className="flex-1 p-4 sm:p-6 lg:p-8">
            <div className="w-full max-w-7xl mx-auto space-y-5 sm:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-24 md:pb-4">
              {children}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Tab Bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white/95 dark:bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-gray-200 dark:border-white/10 flex items-center justify-around h-16 z-[30]">
        {bottomTabs.map(tab => {
          const isActive =
            currentLocation.pathname === tab.path ||
            currentLocation.pathname.startsWith(tab.path + '/');
          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={`flex flex-col items-center justify-center flex-1 h-full gap-1 transition-all active:scale-95 ${isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`}
            >
              <div
                className={`w-10 h-7 flex items-center justify-center rounded-xl transition-all duration-200 ${isActive ? 'bg-emerald-500/15 dark:bg-emerald-400/15' : ''}`}
              >
                <tab.icon size={19} />
              </div>
              <span className="text-[10px] font-bold leading-none">{tab.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Mobile FAB */}
      {isMobile && (
        <>
          {fabOpen && (
            <div className="fixed inset-0 z-40">
              <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => setFabOpen(false)}
              />
              <div className="absolute bottom-[80px] right-4 flex flex-col gap-3 items-end drop-in">
                {fabActions.map((action, i) => (
                  <Link
                    key={action.path}
                    to={action.path}
                    onClick={() => setFabOpen(false)}
                    className="flex items-center gap-3 bg-white dark:bg-[#0f0f0f] border border-gray-200 dark:border-white/10 pr-3 pl-4 py-2 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-sm font-bold text-gray-900 dark:text-white"
                  >
                    {action.label}
                    <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center shadow-md">
                      <action.icon size={14} className="text-white" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={() => setFabOpen(!fabOpen)}
            className="md:hidden fixed bottom-[84px] right-4 z-50 w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white shadow-xl shadow-emerald-500/30 transition-transform active:scale-95"
          >
            <Plus
              size={24}
              className={`transition-transform duration-300 ${fabOpen ? 'rotate-45' : 'rotate-0'}`}
            />
          </button>
        </>
      )}

      <ProfileModal
        open={profileModal}
        initialTab={profileTab}
        onClose={() => setProfileModal(false)}
        onProfileUpdated={refreshGym}
      />
    </div>
  );
};

export default AdminLayout;
